import { useEffect } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

// Etykiety pól formularza wydarzenia (te same klucze co etykiety w formularzu)
const FIELD_LABELS = {
    title: 'event.form.name',
    startDate: 'event.form.startDate',
    startTime: 'event.form.startTime',
    endDate: 'event.form.endDate',
    endTime: 'event.form.endTime',
    address: 'event.form.eventType.address',
    url: 'event.form.eventType.url',
    platform: 'event.form.eventType.platform',
    categories: 'event.form.categories',
    maxParticipants: 'event.form.maxParticipants',
    hostName: 'event.form.host.name',
    hostEmail: 'event.form.host.email',
    hostInfo: 'event.form.host.info',
};

const LABEL_DEFAULTS = {
    url: 'Link do wydarzenia',
    platform: 'Platforma',
    categories: 'Kategorie (maks. 3)',
};

/**
 * Przyklejony pasek na dole formularza wydarzenia:
 * - pokazuje, czego brakuje do zapisu (zamiast wyszarzonego przycisku bez wyjaśnienia),
 * - ostrzega przed zamknięciem karty z niezapisanymi zmianami.
 *
 * extraMissing: dodatkowe braki spoza Formika (np. zdjęcie, bilety).
 */
const FormStatusBar = ({ extraMissing = [], onCancel, submitLabel, isBusy = false, isHidden = false }) => {
    const { t } = useTranslation("default");
    const { errors, isSubmitting, dirty } = useFormikContext();

    const missing = [
        ...Object.keys(errors)
            .filter(key => typeof errors[key] === 'string')
            .map(key => key === 'undefined' || key === ''
                // błąd bez pola = test całego formularza (np. koniec przed początkiem)
                ? errors[key]
                : (FIELD_LABELS[key] ? t(FIELD_LABELS[key], LABEL_DEFAULTS[key] || key) : errors[key])),
        ...extraMissing,
    ];

    useEffect(() => {
        if (!dirty || isSubmitting) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty, isSubmitting]);

    if (isHidden) return null;

    return (
        <Box position="sticky" bottom={0} zIndex={10} mt={6} mx={{ base: '-16px', md: 0 }}
            bg="surface.card" borderTop="1px solid" borderColor="surface.border"
            borderRadius={{ base: 0, md: '18px 18px 0 0' }} boxShadow="0 -8px 24px rgba(14,26,23,0.08)"
            px={{ base: '16px', md: '24px' }} py={{ base: '12px', md: '14px' }}>
            <Flex gap={{ base: '10px', md: '16px' }} alignItems={{ base: 'stretch', md: 'center' }} flexDirection={{ base: 'column', md: 'row' }}>
                <Box flex="1" minW={0} role="status" aria-live="polite">
                    {missing.length > 0 ? (
                        <Text fontSize="14px" color="ink.600" noOfLines={2}>
                            <Text as="span" fontWeight={700} color="orange.700">
                                {t('event.form.missing', 'Do uzupełnienia')}:
                            </Text>{' '}
                            {missing.join(', ')}
                        </Text>
                    ) : (
                        <Text fontSize="14px" fontWeight={600} color="brand.600">
                            {t('event.form.ready', 'Wszystko gotowe do zapisu')}
                        </Text>
                    )}
                </Box>
                <Flex gap="10px" flexShrink={0}>
                    {onCancel && (
                        <Button variant="outline" bg="white" borderColor="surface.control" color="ink.900" h="48px" px="20px"
                            flex={{ base: 1, md: 'none' }} onClick={onCancel}
                            _hover={{ bg: 'surface.page', transform: 'none', boxShadow: 'none' }}>
                            {t('cancel')}
                        </Button>
                    )}
                    <Button type="submit" isLoading={isSubmitting || isBusy} isDisabled={missing.length > 0 || isBusy}
                        bg="brand.500" color="white" h="48px" px="28px" borderRadius="14px" flex={{ base: 2, md: 'none' }}
                        _hover={{ bg: 'brand.600' }}>
                        {submitLabel}
                    </Button>
                </Flex>
            </Flex>
        </Box>
    );
};

export default FormStatusBar;
